import { TableColumnConfig } from "../../config/tableConfig.types";
import { RecordTableColumn } from "./RecordTable.types";
import { getColumnDisplay } from "./tableColumnDisplays";
import { RecordLink } from "./RecordLink";

function linkedEntityRoute(
  column: TableColumnConfig,
  row: Record<string, unknown>,
  entityRoute?: (entityName: string) => string | undefined
) {
  const entityName = column.link?.entityKey ? row[column.link.entityKey] : undefined;

  return typeof entityName === "string" ? entityRoute?.(entityName) : undefined;
}

/**
 * Turns stored column config into the columns `RecordTable` renders. The cell
 * renderers are built here from the names config holds, and nowhere else - shared by
 * the record pages' related tables and by the list pages.
 */
export function configToTableColumns(
  columns: TableColumnConfig[],
  entityRoute?: (entityName: string) => string | undefined
): RecordTableColumn[] {
  return columns.map((column) => {
    const render = getColumnDisplay(column.display);
    const formatted = column.display && column.display !== "text";

    return {
      key: column.key,
      label: column.label,
      ...(column.width === undefined ? {} : { width: column.width }),
      ...(column.minWidth === undefined ? {} : { minWidth: column.minWidth }),
      ...(column.resizable === undefined
        ? {}
        : { resizable: column.resizable }),
      ...(column.sortable === undefined ? {} : { sortable: column.sortable }),
      ...(column.cardSlot || column.display === "badge"
        ? { cardSlot: column.cardSlot ?? "badge" }
        : {}),
      ...(column.cardAttachTo ? { cardAttachTo: column.cardAttachTo } : {}),
      // A plain unlinked column has no accessor at all, so RecordTable renders
      // the raw value exactly as it did before any of this was configurable.
      ...(formatted || column.link
        ? {
            accessor: (row: Record<string, unknown>) => {
              const content = render(row[column.key], column, row);

              // The link wraps whatever the display produced, so a formatted
              // value can also be a link.
              return column.link ? (
                <RecordLink
                  to={column.link.to ?? linkedEntityRoute(column, row, entityRoute)}
                  id={row[column.link.idKey]}
                  text={content}
                  newTab={column.link.newTab}
                />
              ) : (
                content
              );
            },
          }
        : {}),
    };
  });
}
